import AudioPlaybackProtected from './AudioPlaybackProtected';

/**
 * Each audio track is represented as an "AudioSrc"
 *
 * @typedef {object} AudioSrc
 * @property {string} url - The url for the audio source
 * @property {string} format - The audio encoding format for the audio source (e.g. 'ogg', 'mp3', etc..)
 * @property {string} audioType - The audio type for the audio source (AudioType)
 * @property {string} token - The token associated with this audio source
 * @property {string} [key] - The encryption key.  Only set when an audio source is encrypted
 */

/**
 * Instructions for what harness to use and when.
 *
 * @typedef {object} HarnessConfig
 * @property {string} name
 * @property {Array} formats - Formats that this harness supports
 * @property {Array} audioTypes - Exclusive list of audio types this harness supports
 */

/**
 * @typedef {object} AudioPlaybackOptions
 * @property {number} maxProviders
 * @property {[HarnessConfig]} harnessConfigs - An array of objects that map format and audioType to a harness
 * @property {boolean} tryForceFlash
 * @property {Function} logger
 */

/**
 * Instructions for how to create an audio provider
 *
 * @typedef {object} Harness
 * @property {AudioHarnessName} NAME
 * @property {Function} initialize
 * @property {Function} mapMethods
 * @property {Function} mapEventBindings
 * @property {Function} mapEventRemovers
 */

/**
 * AudioPlayback has its own state, separate from redux.
 *
 * @typedef {object} AudioPlaybackState
 * @property {[Harness]} harnesses - An array of harnesses
 * @property {Map} providerRequests - A map of promises that resolve to providers, keyed by the audio source's url
 * @property {Array} activityStack - A stack of audio url's used for determining which urls (and the providers they map to) are most stale and thus can be recycled
 * @property {AudioPlaybackOptions} options - The initial set of options that were passed when the Playback library was initialized.
 */
export let audioPlaybackState = {};

/**
 *
 * @param {AudioPlaybackOptions} options
 * @param {[Harness]} harnesses - An array of harnesses
 * @param {boolean} isForced - true if we're manually triggering re-initializing
 * @returns {AudioPlaybackState}
 */
export function initialize(options, harnesses, isForced) {
  const providersToKeep = isForced ? 0 : 1;
  if (
    audioPlaybackState.activityStack &&
    audioPlaybackState.activityStack.length > providersToKeep
  ) {
    AudioPlaybackProtected.deleteOldestProviders(audioPlaybackState, providersToKeep);
  }
  audioPlaybackState = {
    harnesses,
    providerRequests: audioPlaybackState.providerRequests || new Map(),
    activityStack: audioPlaybackState.activityStack || [],
    options: AudioPlaybackProtected.initOptions(options),
  };

  return audioPlaybackState;
}

/**
 * Gets the provider for the given `audioSrc`
 *
 * @param {AudioSrc} sourceUrl
 * @returns {Promise.<AudioProvider>}
 */
export function get(sourceUrl) {
  return new Promise((resolve, reject) => {
    const providerRequest = audioPlaybackState.providerRequests.get(sourceUrl);

    if (providerRequest) {
      resolve(providerRequest);
    } else {
      reject(new Error(`No provider for ${sourceUrl}`));
    }
  });
}

/**
 *
 * @param {AudioSrc} audioSrc
 * @param {Function} defaultError
 * @param {boolean} isPreload
 * @returns {Promise.<AudioProvider>}
 */
export function load(audioSrc, defaultError, isPreload) {
  return new Promise((resolve, reject) => {
    if (!(audioSrc && audioSrc.url && audioSrc.format && audioSrc.audioType)) {
      reject(
        new Error('`AudioSrc` requires the following fields: `url`, `format`, and `audioType`'),
      );
      return;
    }

    resolve(
      AudioPlaybackProtected.getAudioProvider(
        audioPlaybackState,
        audioSrc,
        defaultError,
        isPreload,
      ),
    );
  });
}

export function preload(audioSrc, defaultError) {
  return load(audioSrc, defaultError, true);
}

export function updateUrl(oldUrl, newUrl) {
  return AudioPlaybackProtected.updateUrl(audioPlaybackState, oldUrl, newUrl);
}

/**
 * deleteProvider - Gets a provider and deletes it. Makes the provider destroy itself and cleans
 * up AudioPlayback state. The major use case is deleting errored out providers after the error has been handled.
 *
 * @param {AudioSrc} audioUrl
 * @returns {Promise}
 */
export function deleteProvider(audioUrl) {
  return AudioPlaybackProtected.destroyAndCleanupProvider(audioPlaybackState, audioUrl);
}
